import { useReducer } from 'react';
import { assoc, isEmpty, map, pipe } from 'ramda';

import { useDeepCompareEffect } from '../utils';

export const initialState = {
  data: [],
  initial: true,
  fetching: false,
  fetched: false,
  error: null,
};

export const mergeWithDependentData = (modifiers, data, dependentData) =>
  pipe(...map(modifier => modifier(dependentData), modifiers))(data);

export function reducer(state = initialState, { type, payload }) {
  switch (type) {
    case 'INIT':
      return { ...state, initial: false, data: payload.data };
    case 'FETCHING':
      return { ...state, fetching: true };
    case 'SUCCESS': {
      return {
        ...state,
        fetching: false,
        fetched: true,
        data: payload.data,
      };
    }
    case 'FAILURE':
      return {
        ...state,
        fetched: true,
        error: payload.message,
      };
    default:
      return state;
  }
}

export default function useDependentData({
  request,
  canWork = true,
  params = {},
  data,
  modifiers,
}) {
  const defaultState = assoc('data', data, initialState);
  const [state, dispatch] = useReducer(reducer, defaultState);

  useDeepCompareEffect(() => {
    const fetchData = async () => {
      if (!isEmpty(data)) {
        dispatch({
          type: 'INIT',
          payload: { data },
        });
      }

      if (canWork) {
        dispatch({
          type: 'FETCHING',
        });

        try {
          const { data: fetchedData } = await request({ ...params });
          const handledData = modifiers.length
            ? mergeWithDependentData(modifiers, data, fetchedData)
            : data;

          dispatch({
            type: 'SUCCESS',
            payload: { data: handledData },
          });
        } catch ({ message }) {
          dispatch({ type: 'FAILURE', payload: message });
        }
      }
    };

    fetchData();
  }, [canWork, params, data]);

  return [state];
}
